import axios from 'axios'
import { logout } from './auth'
import type {
  ApiKey,
  CreateApiKey,
  UpdateApiKey,
  Plan,
  CreateOrUpdatePlan,
  DashboardStats,
  FunctionTestRequest,
  FunctionTestResponse,
  QuickTest,
  ProxyTest,
  BKSearchTest,
  TestConfiguration,
  SynchronizerStatus,
  SynchronizerHistory,
  TriggerSynchronizer,
  SynchronizerOperationResult,
  TableStorageApiKey,
  TableStoragePlan,
  RatelimitSnapshot,
  CreateTableStorageApiKey,
  CreateTableStoragePlan,
  AzureStorageStats,
  SnapshotQuery
} from '@/types'

const api = axios.create({
  baseURL: import.meta.env.VITE_FUNCTION_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: (import.meta.env as any).VITE_FUNCTION_URL?.includes('localhost') ? false : true // Disable credentials for local dev
})

// Request interceptor for logging only - EasyAuth handles authentication
api.interceptors.request.use(
  async (config) => {
    // EasyAuth handles authentication automatically - no Bearer token needed
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`)
    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling and auth
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`)
    return response
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message)
    // Handle 401 unauthorized - logout user
    if (error.response?.status === 401) {
      logout()
    }
    return Promise.reject(error)
  }
)

export const adminApi = {
  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/dashboard')
    return response.data
  },

  // API Keys
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await api.get<ApiKey[]>('/apikeys')
    return response.data
  },

  async getApiKey(id: string): Promise<ApiKey> {
    const response = await api.get<ApiKey>(`/apikeys/${id}`)
    return response.data
  },

  async createApiKey(apiKey: CreateApiKey): Promise<ApiKey> {
    const response = await api.post<ApiKey>('/apikeys', apiKey)
    return response.data
  },

  async provisionApiKey(provisionData: any): Promise<ApiKey> {
    const response = await api.post<ApiKey>('/apikeys/provision', provisionData)
    return response.data
  },

  async deleteApiKeySnapshots(id: string): Promise<any> {
    const response = await api.delete(`/apikeys/${id}/snapshots`)
    return response.data
  },

  async updateApiKey(id: string, apiKey: UpdateApiKey): Promise<ApiKey> {
    const response = await api.put<ApiKey>(`/apikeys/${id}`, apiKey)
    return response.data
  },

  async deleteApiKey(id: string): Promise<void> {
    await api.delete(`/apikeys/${id}`)
  },

  // Plans
  async getPlans(): Promise<Plan[]> {
    const response = await api.get<Plan[]>('/plans')
    return response.data
  },

  async getPlan(name: string): Promise<Plan> {
    const response = await api.get<Plan>(`/plans/${name}`)
    return response.data
  },

  async createPlan(plan: CreateOrUpdatePlan): Promise<Plan> {
    const response = await api.post<Plan>('/plans', plan)
    return response.data
  },

  async updatePlan(name: string, plan: CreateOrUpdatePlan): Promise<Plan> {
    const response = await api.put<Plan>(`/plans/${name}`, plan)
    return response.data
  },

  async deletePlan(name: string): Promise<void> {
    await api.delete(`/plans/${name}`)
  },

  // Function Testing
  async getTestConfiguration(): Promise<TestConfiguration> {
    const response = await api.get<TestConfiguration>('/test/config')
    return response.data
  },

  async testFunction(request: FunctionTestRequest): Promise<FunctionTestResponse> {
    const response = await api.post<FunctionTestResponse>('/test/function', request)
    return response.data
  },

  async testGateEndpoint(request: QuickTest): Promise<FunctionTestResponse> {
    const response = await api.post<FunctionTestResponse>('/test/gate', request)
    return response.data
  },

  async testProxyEndpoint(request: ProxyTest): Promise<FunctionTestResponse> {
    const response = await api.post<FunctionTestResponse>('/test/proxy', request)
    return response.data
  },

  async testBKSearchDirect(request: BKSearchTest): Promise<FunctionTestResponse> {
    const response = await api.post<FunctionTestResponse>('/test/bksearch', request)
    return response.data
  },

  // Redis Management
  async clearRedisCache(): Promise<{ message: string; keysCleared: number }> {
    const response = await api.post<{ message: string; keysCleared: number }>('/redis/clear')
    return response.data
  },

  async cleanupOrphanedRateLimitKeys(): Promise<{ message: string; keysDeleted: number; deletedKeys?: string[] }> {
    const response = await api.post<{ message: string; keysDeleted: number; deletedKeys?: string[] }>('/redis/cleanup-orphaned')
    return response.data
  },

  async getRedisStats(): Promise<any> {
    const response = await api.get<any>('/redis/stats')
    return response.data
  },

  // Synchronizer Management
  async getSynchronizerStatus(): Promise<SynchronizerStatus> {
    const response = await api.get<SynchronizerStatus>('/synchronizer/status')
    return response.data
  },

  async getSynchronizerHistory(take?: number): Promise<SynchronizerHistory[]> {
    const params = take ? { take } : {}
    const response = await api.get<SynchronizerHistory[]>('/synchronizer/history', { params })
    return response.data
  },

  async triggerSnapshot(request?: TriggerSynchronizer): Promise<SynchronizerOperationResult> {
    const response = await api.post<SynchronizerOperationResult>('/synchronizer/trigger/snapshot', request || {})
    return response.data
  },

  async triggerRestore(request?: TriggerSynchronizer): Promise<SynchronizerOperationResult> {
    const response = await api.post<SynchronizerOperationResult>('/synchronizer/trigger/restore', request || {})
    return response.data
  },

  async clearSynchronizerHistory(): Promise<void> {
    await api.delete('/synchronizer/history')
  },

  async getSynchronizerConfig(): Promise<any> {
    const response = await api.get<any>('/synchronizer/config')
    return response.data
  },

  // Azure Storage Management
  async getStorageStats(): Promise<AzureStorageStats> {
    const response = await api.get<AzureStorageStats>('/storage/stats')
    return response.data
  },

  async testStorageConnection(): Promise<{ connected: boolean; timestamp: string; error?: string }> {
    const response = await api.get<{ connected: boolean; timestamp: string; error?: string }>('/storage/test-connection')
    return response.data
  },

  async getStorageApiKeys(): Promise<TableStorageApiKey[]> {
    const response = await api.get<TableStorageApiKey[]>('/storage/apikeys')
    return response.data
  },

  async getStorageApiKey(id: string): Promise<TableStorageApiKey> {
    const response = await api.get<TableStorageApiKey>(`/storage/apikeys/${id}`)
    return response.data
  },

  async upsertStorageApiKey(apiKey: CreateTableStorageApiKey): Promise<TableStorageApiKey> {
    const response = await api.post<TableStorageApiKey>('/storage/apikeys', apiKey)
    return response.data
  },

  async deleteStorageApiKey(id: string): Promise<void> {
    await api.delete(`/storage/apikeys/${id}`)
  },

  async getStoragePlans(): Promise<TableStoragePlan[]> {
    const response = await api.get<TableStoragePlan[]>('/storage/plans')
    return response.data
  },

  async getStoragePlan(name: string): Promise<TableStoragePlan> {
    const response = await api.get<TableStoragePlan>(`/storage/plans/${name}`)
    return response.data
  },

  async upsertStoragePlan(plan: CreateTableStoragePlan): Promise<TableStoragePlan> {
    const response = await api.post<TableStoragePlan>('/storage/plans', plan)
    return response.data
  },

  async deleteStoragePlan(name: string): Promise<void> {
    await api.delete(`/storage/plans/${name}`)
  },

  async getSnapshots(query?: SnapshotQuery): Promise<RatelimitSnapshot[]> {
    const response = await api.get<RatelimitSnapshot[]>('/storage/snapshots', { params: query })
    return response.data
  },

  async deleteAllSnapshots(): Promise<{ success: boolean; message: string; deletedCount: number }> {
    const response = await api.delete<{ success: boolean; message: string; deletedCount: number }>('/storage/snapshots')
    return response.data
  }
}

export default adminApi
